import type {
    ActiveMediaDevices,
    MediaAudioInputDevice,
    MediaAudioInputQuery,
    MediaDevicesChangedEvent,
    MediaInputDevice,
    MediaVideoInputDevice,
    MediaVideoInputQuery,
    RequestedMediaDevices,
    SelectedMediaDevices,
} from '@yandex-360/telemost-sdk/device-manager';
import type {
    ActiveDevices,
    DevicesChangedEvent,
    InputDevice,
    RequestedDevices,
    SelectedDevices,
    SessionEvent,
} from '@yandex-video-platform/goloom-sdk';

import {
    mapAudioInputDeviceFromGoloom,
    mapAudioInputQueryFromGoloom,
    mapOutputDeviceFromGoloom,
    mapVideoInputDeviceFromGoloom,
    mapVideoInputQueryFromGoloom,
} from './goloom-device-mappers';

type GoloomAudioInputSelection = NonNullable<SelectedDevices['audioInput']>;
type GoloomVideoInputSelection = NonNullable<SelectedDevices['videoInput']>;

const mapInputDeviceFromGoloom = (device: InputDevice): MediaInputDevice =>
    device.kind === 'audioinput' ? mapAudioInputDeviceFromGoloom(device) : mapVideoInputDeviceFromGoloom(device);

const mapAudioInputSelectionFromGoloom = (
    device: GoloomAudioInputSelection
): MediaAudioInputQuery | MediaAudioInputDevice =>
    'track' in device ? mapAudioInputDeviceFromGoloom(device) : mapAudioInputQueryFromGoloom(device);

const mapVideoInputSelectionFromGoloom = (
    device: GoloomVideoInputSelection
): MediaVideoInputQuery | MediaVideoInputDevice =>
    'track' in device ? mapVideoInputDeviceFromGoloom(device) : mapVideoInputQueryFromGoloom(device);

export const mapActiveDevicesFromGoloom = (activeDevices: ActiveDevices): ActiveMediaDevices => ({
    audioInput: activeDevices.audioInput ? mapAudioInputDeviceFromGoloom(activeDevices.audioInput) : null,
    videoInput: activeDevices.videoInput ? mapVideoInputDeviceFromGoloom(activeDevices.videoInput) : null,
    audioOutput: activeDevices.audioOutput ? mapOutputDeviceFromGoloom(activeDevices.audioOutput) : null,
});

export const mapSelectedDevicesFromGoloom = (selectedDevices: SelectedDevices): SelectedMediaDevices => ({
    audioInput: selectedDevices.audioInput ? mapAudioInputSelectionFromGoloom(selectedDevices.audioInput) : null,
    videoInput: selectedDevices.videoInput ? mapVideoInputSelectionFromGoloom(selectedDevices.videoInput) : null,
    audioOutput: selectedDevices.audioOutput ? mapOutputDeviceFromGoloom(selectedDevices.audioOutput) : null,
});

export const mapRequestedDevicesFromGoloom = (devices: RequestedDevices): RequestedMediaDevices => ({
    audioInput: devices.audioInput.map(mapAudioInputDeviceFromGoloom),
    videoInput: devices.videoInput.map(mapVideoInputDeviceFromGoloom),
    audioOutput: devices.audioOutput.map(mapOutputDeviceFromGoloom),
});

export const mapDeviceChangedFromGoloom = (event: DevicesChangedEvent): MediaDevicesChangedEvent => ({
    added: event.added.map((device) =>
        device.kind === 'audiooutput' ? mapOutputDeviceFromGoloom(device) : mapInputDeviceFromGoloom(device)
    ),
    removed: event.removed.map((device) =>
        device.kind === 'audiooutput' ? mapOutputDeviceFromGoloom(device) : mapInputDeviceFromGoloom(device)
    ),
});

export const mapActiveDevicesChangedFromGoloom = (
    event: SessionEvent['activeDevicesChanged']
): { activeDevices: ActiveMediaDevices } => ({
    activeDevices: mapActiveDevicesFromGoloom(event.activeDevices),
});
